import { TimelineEventKind } from '../../../slices/marketplace/timeline/models/enums/timeline-event-kind.enum';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import {
    getBusinessDayNumberPt,
    getBusinessDayStartUtc,
    getDayOffset,
} from '../../../slices/marketplace/timeline/utils/timeline-dates';

const MIN_CAPPED_DURATION_DAYS = 1;
const DAYS_BEFORE_CLOSING = 1;

export interface ClosingLimit {
    closingOffset: number | null;
    lastStage: number | null;
}

export interface CreateClosingLimitOptions {
    events: TimelineEvent[];
    closingDate?: string | null;
    engagementStartDate?: Date;
}

export interface ClosingCapEligibilityOptions {
    event: TimelineEvent;
    limit: ClosingLimit;
}

export interface ApplyClosingCapOptions extends ClosingCapEligibilityOptions {
    dayStart: number;
    duration: number;
}

export type ClosingCappedTiming = Pick<TimelineEvent, 'dayEnd' | 'closingLimitedDurationDays'>;

const getClosingOffset = (options: CreateClosingLimitOptions): number | null =>
    options.engagementStartDate && options.closingDate
        ? getDayOffset(options.closingDate, options.engagementStartDate)
        : null;

const getLastStage = (events: TimelineEvent[]): number | null =>
    events.length > 0 ? events[events.length - 1].stage : null;

export const createClosingLimit = (options: CreateClosingLimitOptions): ClosingLimit => ({
    closingOffset: getClosingOffset(options),
    lastStage: getLastStage(options.events),
});

export const isEligibleForClosingCap = (options: ClosingCapEligibilityOptions): boolean => {
    const { event, limit } = options;
    return (
        limit.closingOffset !== null &&
        event.stage === limit.lastStage &&
        event.kind !== TimelineEventKind.BREAK_MARKER &&
        !event.retiredAtUtc &&
        (event.status === TimelineEventStatus.Upcoming ||
            event.status === TimelineEventStatus.InProgress)
    );
};

export const getLastDayBeforeClosingUtc = (closingDate: string): Date =>
    getBusinessDayStartUtc(getBusinessDayNumberPt(closingDate) - DAYS_BEFORE_CLOSING);

export const countShortenedEvents = (events: TimelineEvent[]): number =>
    events.filter((event) => event.closingLimitedDurationDays != null).length;

export const applyClosingCap = (options: ApplyClosingCapOptions): ClosingCappedTiming => {
    const { event, dayStart, duration, limit } = options;
    const uncappedDayEnd = dayStart + duration;
    if (!isEligibleForClosingCap({ event, limit })) {
        return { dayEnd: uncappedDayEnd, closingLimitedDurationDays: null };
    }
    const cappedDayEnd = Math.max(
        dayStart + MIN_CAPPED_DURATION_DAYS,
        Math.min(uncappedDayEnd, limit.closingOffset)
    );
    return {
        dayEnd: cappedDayEnd,
        closingLimitedDurationDays:
            cappedDayEnd < uncappedDayEnd ? cappedDayEnd - dayStart : null,
    };
};
